import { cx } from 'emotion';
import React, { useRef, useState, useEffect } from 'react';

import { useGameTimer } from '@ezugi/bootstrap/src/hooks/useGameTimer';
import { useRoundStatus } from '@ezugi/bootstrap/src/hooks/useRoundStatus';
import { PLACE_YOUR_BETS } from '@ezugi/constants/src';
import { DeviceInfo, isEvoWrapped, ss } from '@ezugi/utils';

import {
  DEFAULT_DURATION,
  WINNER_LIST_DEFAULT_COLOR,
  WINNER_LIST_TOP_WINNERS_COLOR,
  NUMBER_OF_HIGHLIGHTED_WINNERS,
  WINNER_USERNAME_BREAKPOINT,
  WINNER_LIST_ROW_HEIGHT,
  WINNER_LIST_ITEM_DISPLAY_TIMER,
  WINNER_LIST_INDEX,
  MOBILE_MULTI_ROW_MAX_NUMBER_OF_WINNERS_PER_SECOND,
} from './constants';
import { PlayerIcon } from './icons';
import {
  Container,
  AnimationWrapper,
  rowClassName,
  mobileRow,
  overflowClassName,
  desktopClassName,
  mobileClassName,
  mobileNewStyleClassName,
  wlHeaderClassName,
  mobileWlClassName,
  mobileAnimationWrapperClassName,
  fadeOutAnimation,
  mobileMultiRowWithFadeOutAnumationClassName as mobileMultiClassName,
} from './style';

type Winner = {
  Nickname: string;
  WinAmount: string | number;
  style?: React.CSSProperties;
}

type Header = {
  label: string;
  amount: string | number;
}

type WinnerListProps = {
  list: Winner[];
  duration?: number | string;
  header?: Header;
  maxLength?: number;
  isMobileMultiRow?: boolean;
  className: string;
}

const formatWinnersName = (name: string) => {
  if (name.length > WINNER_USERNAME_BREAKPOINT) return `${name.slice(0, WINNER_USERNAME_BREAKPOINT)}...`;
  return name;
};

const getWrapperStyle = (isMobileMultiRow: boolean, nrOfWinners: number, duration: number) => {
  const evoWrapped = isEvoWrapped();
  const style: React.CSSProperties & Record<`--${string}`, number | string> = {
    '--duration': `${duration}ms`,
  };
  if (!isMobileMultiRow) return style;

  let animationSpeedRate = 1;
  if (nrOfWinners <= 10) {
    animationSpeedRate = duration / DEFAULT_DURATION;
  } else {
    const maxNrOfWinners = (duration / 1000) * MOBILE_MULTI_ROW_MAX_NUMBER_OF_WINNERS_PER_SECOND;
    const isExceedingMaxNrOfWinners = nrOfWinners > maxNrOfWinners;
    if (isExceedingMaxNrOfWinners) {
      const winnersRatio = maxNrOfWinners / nrOfWinners;
      animationSpeedRate = Math.round(winnersRatio * 100) / 100;
    } else {
      const timeToComplete = nrOfWinners * (1 / MOBILE_MULTI_ROW_MAX_NUMBER_OF_WINNERS_PER_SECOND) * 1000;
      animationSpeedRate = timeToComplete < DEFAULT_DURATION ? 1 : duration / timeToComplete;
    }
  }
  if (evoWrapped) {
    animationSpeedRate = 1;
  }
  style['--animationSpeedRate'] = animationSpeedRate;
  return style;
};

const rowRenderer = (
  label: string,
  amount: string | number,
  style: React.CSSProperties,
  isSingleRowVariant: boolean
) => (
  <div
    className={cx(rowClassName, { [mobileRow]: isSingleRowVariant })}
    data-name="winner-list-row"
    key={`${label}-${amount}`}
    style={style}
  >
    <span className="winner-list-amount">{amount}</span>
    <span className={cx('winner-list-label', overflowClassName)}>{formatWinnersName(label)}</span>
  </div>
);
const WinnerList = ({
  list = [],
  className,
  header,
  duration = DEFAULT_DURATION,
  maxLength = Infinity,
  isMobileMultiRow = false,
  ...rest
}: WinnerListProps) => {
  const listRef = useRef<HTMLDivElement>(null);
  const [ currentItem, setCurrentItem ] = useState(0);

  const numberOfWinners = list.length;

  const isMobileSingleRow = DeviceInfo.isHandheld && !isMobileMultiRow;

  const { timeLeft } = useGameTimer();
  const { raw: roundStatus } = useRoundStatus();

  const maxDisplayableWinners = isMobileSingleRow
    ? Math.floor(+duration / WINNER_LIST_ITEM_DISPLAY_TIMER) - 1 : Infinity;

  const finalList = list.slice(0, Math.min(maxLength, maxDisplayableWinners));

  const wrapperStyle = getWrapperStyle(isMobileMultiRow, numberOfWinners, +duration);

  const wrapperMobileStyle = { transform: `translateY(calc(100% - ${currentItem * WINNER_LIST_ROW_HEIGHT}px))` };

  useEffect(() => {
    if (isMobileSingleRow) {
      const [ err, sessionTimer ] = ss.get(WINNER_LIST_INDEX);

      if (sessionTimer && !err) {
        setCurrentItem(Number(sessionTimer));
      }
    }

    const scrollInterval = setInterval(() => setCurrentItem((prev) => prev + 1), WINNER_LIST_ITEM_DISPLAY_TIMER);

    return () => {
      if (isMobileSingleRow) {
        ss.del(WINNER_LIST_INDEX);

        setCurrentItem((prev) => {
          if (prev <= finalList.length) {
            ss.put(WINNER_LIST_INDEX, prev);
          }
          return prev;
        });
      }

      clearInterval(scrollInterval);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container
      {...rest}
      data-name="winner-list"
      data-duration={duration}
      className={cx(
        className,
        {
          [mobileClassName]: isMobileSingleRow,
          [desktopClassName]: !DeviceInfo.isHandheld,
          [mobileNewStyleClassName]: DeviceInfo.isHandheld && isMobileMultiRow,
          [fadeOutAnimation]: timeLeft <= 5000 && roundStatus === PLACE_YOUR_BETS && isEvoWrapped(),
        }
      )}
    >
      <AnimationWrapper
        key={finalList.toString()}
        ref={listRef}
        className={cx({
          [mobileAnimationWrapperClassName]: isMobileSingleRow,
          [mobileMultiClassName]: isMobileMultiRow && DeviceInfo.isHandheld,

        })}
        style={isMobileSingleRow ? wrapperMobileStyle : wrapperStyle}
      >
        {header ? (
          <div
            className={cx(
              rowClassName,
              wlHeaderClassName,
              {
                [mobileRow]: isMobileSingleRow,
                [mobileWlClassName]: isMobileSingleRow,
              }
            )}
            data-name="winner-list-row"
          >
            <span>{numberOfWinners}</span>
            <PlayerIcon />
            <span>{header.amount}</span>
          </div>
        ) : null}
        {finalList.map((
          { Nickname,
            WinAmount,
            style },
          index
        ) => rowRenderer(
          Nickname,
          WinAmount,
          { ...style,
            color: index < NUMBER_OF_HIGHLIGHTED_WINNERS
              ? WINNER_LIST_TOP_WINNERS_COLOR
              : WINNER_LIST_DEFAULT_COLOR },
          isMobileSingleRow
        ))}
      </AnimationWrapper>
    </Container>
  );
};

export default WinnerList;
